import { memo, useCallback, useEffect, useMemo, useState } from "react";
import useCountries from "../../../../hooks/useCountries";
import { useUser } from "../../../../../../../contexts/userContext/userContext";
import usePurchases from "../../../../hooks/usePurchases";
import Form from "../../../../../../Reusable/Form/Form";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import SelectElement from "../../../../../../Elements/SelectElement/SelectElement";
import {
  BillingDataT,
  CountryT,
  PackageTypeRouteT,
} from "../../../../models/models";
import useTransaction from "../../../../hooks/useTransaction";
import { useParams } from "react-router-dom";
import useCreditCard from "../../../../hooks/useCreditCard";

import styles from "./styles.module.scss";
import {
  EMAIL_VALIDATOR,
  PHONE_VALIDATOR,
} from "../../../../../../../helpers/validators";

const BillingDataForm = memo(() => {
  const { packageType } = useParams<{
    packageType: PackageTypeRouteT;
  }>();
  const { getTextByKey } = useContent();
  const { countries, fetchCountries } = useCountries();
  const { getUserData } = useUser();
  const { goToThirdStep } = usePurchases();
  const { createTransaction } = useTransaction();
  const { paymentType } = useCreditCard();

  // I'm using local state for country input,
  // because I need not average select input from Form component,
  // But specific select from SelectElement component
  // and I'm not sure it can be controlled via form controller.
  const [country, setCountry] = useState(getUserData()?.UserCountry);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const countriesSelectList = useMemo(
    () => countries.map((country: CountryT) => country.countryKey),
    [countries]
  );

  const fields = {
    firstName: {
      label: "purchase-first-name-label",
      placeholder: "purchase-first-name-label",
      fieldKey: "firstName",
      element: "input",
      type: "text",
      validation: {
        required: true,
        invalidMessage: "default-input-invalid-message",
      },
    },
    lastName: {
      label: "purchase-last-name-label",
      placeholder: "purchase-last-name-label",
      fieldKey: "lastName",
      element: "input",
      type: "text",
      validation: {
        required: true,
        invalidMessage: "default-input-invalid-message",
      },
    },
    country: {
      element: "customelement",
      render: () => (
        <div className={styles.countryField}>
          <div>{getTextByKey("purchase-country-label")}</div>
          <SelectElement
            item={country}
            setItem={setCountry}
            list={countriesSelectList}
            showArrowIcon
          />
        </div>
      ),
    },
    email: {
      label: "purchase-email-label",
      fieldKey: "email",
      placeholder: "purchase-email-label",
      element: "input",
      type: "email",
      validation: {
        required: true,
        invalidMessage: "invalid-email-text",
        reg: EMAIL_VALIDATOR,
      },
    },
    phone: {
      label: "purchase-phone-label",
      placeholder: "purchase-phone-label",
      fieldKey: "phone",
      element: "input",
      type: "tel",
      validation: {
        required: true,
        reg: PHONE_VALIDATOR,
        invalidMessage: "invalid-phone-text",
      },
    },
    separator: {
      element: "customelement",
      fieldKey: "separator",
      render: () => (
        <div className={styles.formSeparator}>
          <hr />
          <div className={styles.formSubHeader}>
            <span />
            <h2>{getTextByKey("invoice-settings-title")}</h2>
          </div>
        </div>
      ),
    },
    nameForInvoice: {
      label: "purchase-invoice-name-label",
      placeholder: "purchase-invoice-name-label",
      fieldKey: "nameForInvoice",
      element: "input",
      type: "text",
      validation: {
        required: true,
        invalidMessage: "default-input-invalid-message",
      },
    },
    idForInvoice: {
      label: "purchase-company-id-label",
      placeholder: "purchase-company-id-label",
      fieldKey: "idForInvoice",
      element: "input",
      type: "text",
    },
  };

  const handleSubmit = useCallback(
    (data: Omit<BillingDataT, "country">) => {
      const payload: BillingDataT = {
        ...data,
        country: countries.find((el) => el.countryKey === country)
          ?.countryValue!,
      };
      goToThirdStep(payload);

      if (packageType === "pay-as-you-go") {
        createTransaction();
      }
    },
    [countries, country]
  );

  return (
    <div className={styles.paymentSettingsWrapper}>
      <Form
        // @ts-ignore
        fields={fields}
        onSubmit={handleSubmit}
        submitProps={{
          title: getTextByKey(
            packageType === "pay-as-you-go"
              ? paymentType !== "paypal"
                ? "continue-payment"
                : "continue-to-paypal"
              : "purchase-next"
          ),
        }}
        defaultValuesObj={{
          firstName: getUserData()?.UserFirstName,
          lastName: getUserData()?.UserLastName,
          email: getUserData()?.UserName,
          phone: getUserData()?.UserPhone,
          nameForInvoice:
            getUserData()?.User_Invoice_Name ||
            getUserData()?.UserFirstName + " " + getUserData()?.UserLastName,
          idForInvoice: getUserData()?.User_Invoice_Id,
        }}
      />
    </div>
  );
});

export default BillingDataForm;
